import { useDisclosureContext } from '@vivli/features/disclosures/infrastructure/context';
import { IDisclosure } from '@vivli/features/disclosures/infrastructure/interface';
import { CheckboxFormFieldComponent } from '@vivli/shared/components';
import { useToastApiContext } from '@vivli/core/infrastructure/context';
import { useCopyToClipboardHook } from '@vivli/shared/infrastructure/hook';
import { CSSProperties, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { ButtonView } from '@vivli/shared/views';
import { DisclosureStatusEnum } from '@vivli/features/disclosures/infrastructure/enum';
import { DataPackageUploaderComponent } from '@vivli/shared/features/data-package/components';

const containerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateRows: 'auto',
    rowGap: '30px'
};

const acknowledgementContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateRows: 'auto',
    rowGap: '30px'
};

const acknowledgementStyle: CSSProperties = {
    boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 5px, rgba(0, 0, 0, 0.1) 0px 1px 5px',
    padding: '20px',
    background: 'rgba(255, 255, 255, 0.5)',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    columnGap: '20px',
    alignItems: 'center'
};

const headerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    columnGap: '1.5em'
};

const checkboxContainerStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'auto auto',
    rowGap: '1em',
    justifyItems: 'start'
};

export const DocumentsForReviewFormView = () => {
    const {
        permissions,
        acknowledgementText,
        user,
        organizations,
        documentsForReviewUri,
        disclosure,
        refreshApiToken,
        reviewDocsPackageApi
    } = useDisclosureContext();
    const { files, setAdditionalFileProps } = reviewDocsPackageApi;
    const titleText = 'Please include the copied text in your publication document before you upload it.';

    const formApi = useFormContext<IDisclosure>();
    const acknowledgementInDocument = useWatch({ name: 'acknowledgementInDocument' });
    const acknowledgementElsewhere = useWatch({ name: 'acknowledgementElsewhere' });

    const { success, error } = useToastApiContext();
    const { copyToClipboard } = useCopyToClipboardHook();

    const handleCopyAcknowledgmentText = () => {
        copyToClipboard(acknowledgementText).then(() => {
            success('Acknowledgement Text copied to your clipboard!');
        });
    };

    const handleFileLimitWarning = () => {
        error('A maximum of 2 review documents may be uploaded.');
    };

    const allowUpload = useMemo(() => {
        return disclosure.status === DisclosureStatusEnum.Draft && (!files || files?.length < 2);
    }, [disclosure.status, files]);

    useEffect(() => {
        if (!organizations || user?.orgMemberships.length === 0) {
            return;
        }

        const orgMembership = user.orgMemberships[0];
        const org = organizations.find((o) => orgMembership.orgId === o.id);

        // set additional file props to include org name and id
        setAdditionalFileProps({
            organizationId: org?.id,
            organizationName: org?.name
        });
    }, [user, organizations]);

    useEffect(() => {
        if (acknowledgementInDocument) {
            formApi.setValue('acknowledgementElsewhere', false);
        }
    }, [acknowledgementInDocument]);

    useEffect(() => {
        if (acknowledgementElsewhere) {
            formApi.setValue('acknowledgementInDocument', false);
        }
    }, [acknowledgementElsewhere]);

    return (
        <div style={containerStyle}>
            <div style={acknowledgementContainerStyle}>
                <div>Please include the following text in your publication document before you upload it.</div>
                <div style={acknowledgementStyle}>
                    {acknowledgementText}
                    {permissions.editReviewDocs && (
                        <div>
                            <ButtonView title={titleText} onClick={handleCopyAcknowledgmentText} style={{ width: '200px' }}>
                                Copy Acknowledgement
                            </ButtonView>
                        </div>
                    )}
                </div>
                <div style={checkboxContainerStyle}>
                    <CheckboxFormFieldComponent
                        name="acknowledgementInDocument"
                        label="I have included acknowledgment as required in the Vivli DUA"
                        reverseOrientation={true}
                        disabled={!permissions.editReviewDocs}
                    />
                    <CheckboxFormFieldComponent
                        name="acknowledgementElsewhere"
                        label="I am unable to include the acknowledgement as there is a word limit in the abstract. I will include the acknowledgment in the poster or oral presentation if selected"
                        reverseOrientation={true}
                        disabled={!permissions.editReviewDocs}
                    />
                </div>
            </div>
            {(acknowledgementInDocument || acknowledgementElsewhere) && (
                <>
                    <div style={headerStyle}>
                        {permissions.uploadReviewDocs && (
                            <>
                                <div>
                                    Combine the documents into 1 or maximum of 2 files (e.g. 1 file containing the final draft of your
                                    publication and 1 file containing your attachments such as supplemental figures, graphs, etc.).
                                </div>
                            </>
                        )}
                    </div>
                    <div>
                        <DataPackageUploaderComponent
                            user={user}
                            uploadUri={documentsForReviewUri}
                            onFileLimitWarning={handleFileLimitWarning}
                            allowUpload={allowUpload && permissions.uploadReviewDocs}
                            allowDownload={permissions.uploadReviewDocs || permissions.downloadReviewDocs}
                            allowDelete={permissions.uploadReviewDocs}
                            hideFileType={true}
                            refreshApiToken={refreshApiToken}
                            showUploadDate={true}
                            {...reviewDocsPackageApi}
                        />
                    </div>
                </>
            )}
        </div>
    );
};
